<template>
  <div class="requirement-management">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="el-loading-spinner">
        <i class="el-icon-loading"></i>
        <p class="el-loading-text">加载中...</p>
      </div>
    </div>
    
    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      <el-alert :title="error" type="error" :closable="false" show-icon />
    </div>

    <!-- 数据统计 -->
    <div class="stats-info" v-if="!loading && !error">
      <p>共找到 {{ requirements.length }} 条需求</p>
    </div>

    <!-- 需求卡片列表 -->
    <div class="requirements-grid">
      <el-card 
        v-for="item in requirements" 
        :key="item.id" 
        class="requirement-card"
        :class="{ 'is-rejected': item.status === 'rejected' }"
        @click="showDetail(item)"
      >
        <template #header>
          <div class="card-header">
            <span class="title">需求 #{{ item.id }}</span>
            <el-tag :type="getStatusType(item.status)" size="small">
              {{ getStatusText(item.status) }}
            </el-tag>
          </div>
        </template>
        
        <div class="card-content">
          <div class="user-info">
            <p><strong>提交用户:</strong> {{ item.user_name }}</p>
            <p><strong>用户ID:</strong> {{ item.user_id }}</p>
            <p><strong>联系电话:</strong> {{ item.contact }}</p>
          </div>
          
          <div class="requirement-info">
            <p class="description">{{ item.description }}</p>
            <p class="time">提交时间: {{ formatTime(item.created_at) }}</p>
          </div>
          
          <div class="preview-images" v-if="item.image_urls">
            <el-image 
              v-for="(img, index) in getImages(item.image_urls)"
              :key="index"
              :src="img"
              fit="cover"
              class="preview-image"
              :preview-src-list="getImages(item.image_urls)"
              :initial-index="0"
              @error="handleImageError"
            />
          </div>
        </div>
        
        <div class="card-actions">
          <el-button-group>
            <el-button 
              size="small"
              :type="item.status === 'submitted' || item.status === 'pending' ? 'primary' : 'info'"
              :disabled="item.status !== 'submitted' && item.status !== 'pending'"
              @click.stop="handleProcess(item)"
            >处理</el-button>
            <el-button 
              size="small"
              type="success"
              :disabled="item.status !== 'accepted' && item.status !== 'processing'"
              @click.stop="handleComplete(item)"
            >完成</el-button>
            <el-button 
              size="small" 
              type="danger"
              :disabled="item.status !== 'submitted' && item.status !== 'pending'"
              @click.stop="handleReject(item)"
            >拒绝</el-button>
            <el-button
              size="small"
              type="danger"
              plain
              @click.stop="handleDelete(item)"
            >删除</el-button>
          </el-button-group>
        </div>
      </el-card>
    </div>

        <!-- 分页器 -->
    <div class="pagination">
      <el-pagination
        v-model:current-page="currentPage"
        :page-size="10"
        :total="total"
        layout="total, prev, pager, next"
        @current-change="handlePageChange"
      />
    </div>


    <!-- 详情弹窗 -->
    <el-dialog
      v-model="detailVisible"
      :title="`需求详情 #${currentRequirement?.id}`"
      width="700px"
    >
      <div v-if="currentRequirement" class="requirement-detail">
        <div class="detail-section">
          <h4>用户信息</h4>
          <p><strong>用户名:</strong> {{ currentRequirement.user_name }}</p>
          <p><strong>用户ID:</strong> {{ currentRequirement.user_id }}</p>
          <p><strong>联系电话:</strong> {{ currentRequirement.contact }}</p>
        </div>
        
        <div class="detail-section">
          <h4>需求信息</h4>
          <p><strong>状态:</strong> 
            <el-tag :type="getStatusType(currentRequirement.status)">
              {{ getStatusText(currentRequirement.status) }}
            </el-tag>
          </p>
          <p><strong>提交时间:</strong> {{ formatTime(currentRequirement.created_at) }}</p>
          <p v-if="currentRequirement.reject_reason">
            <strong>拒绝原因:</strong> {{ currentRequirement.reject_reason }}
          </p>
          <div class="description-box">
            <strong>需求描述:</strong>
            <p>{{ currentRequirement.description }}</p>
          </div>
        </div>
        
        <div class="detail-section">
          <h4>参考图片</h4>
          <div class="detail-images">
            <el-image 
              v-for="(img, index) in getImages(currentRequirement.image_urls)"
              :key="index"
              :src="img"
              fit="contain"
              :preview-src-list="getImages(currentRequirement.image_urls)"
              :initial-index="index"
            />
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import type { RequirementItem } from '@/types/custom'
import { checkAuth } from '@/utils/authDebug'
import requestV2 from '@/utils/requestV2';

const currentPage = ref(1)
const total = ref(0)
const requirements = ref<RequirementItem[]>([])
const currentRequirement = ref<RequirementItem | null>(null)
const processDialogVisible = ref(false)
const rejectDialogVisible = ref(false)
const rejectForm = ref({
  reason: ''
})
const loading = ref(false)
const error = ref('')

// 详情弹窗相关
const detailVisible = ref(false)




// 处理分页
const handlePageChange = (page: number) => {
  currentPage.value = page
  fetchRequirements()
}

// 获取需求列表
const fetchRequirements = async () => {
  loading.value = true
  error.value = ''
  
  try {
    console.log('开始获取需求列表...')
    const response = await requestV2.get(`/system/custom/requirements?page=${currentPage.value}`)
    
    if (!(response.data.code == 200)) {
      throw new Error(`请求失败: ${response.status} ${response.statusText}`)
    }
    
    const data = await response.data.data;
    console.log('获取到的需求数据:', data)
    
    requirements.value = data.records;
    total.value = data.total;
    
    if (data.length === 0) {
      console.log('没有找到任何需求')
    } else {
      console.log('需求列表:', requirements.value)
      // 检查第一个需求的图片数据
      const firstItem = requirements.value[0]
      console.log('第一个需求的图片数据:', {
        raw: firstItem.reference_images,
        processed: getImages(firstItem.reference_images)
      })
    }
  } catch (err) {
    console.error('获取需求列表失败:', err)
    error.value = err instanceof Error ? err.message : '获取需求列表失败'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

// 显示详情
const showDetail = (requirement: RequirementItem) => {
  currentRequirement.value = requirement
  detailVisible.value = true
}

// 处理需求
const handleProcess = async (item: RequirementItem) => {
  try {
    const response = await requestV2.post(`/system/custom/requirements/${item.id}/status`, JSON.stringify({
        status: 'accepted'
      }))
    
    if (!(response.data.code == 200)) {
      throw new Error('处理需求失败')
    }
    
    ElMessage.success('需求已接受')
    await fetchRequirements()
  } catch (error) {
    console.error('处理需求失败:', error)
    ElMessage.error('处理需求失败')
  }
}

// 完成需求
const handleComplete = async (item: RequirementItem) => {
  try {
    const response = await requestV2.post(`/system/custom/requirements/${item.id}/status`, JSON.stringify({
        status: 'completed'
      }))
    
    if (!(response.data.code == 200)) {
      throw new Error('完成需求失败')
    }
    
    ElMessage.success('需求已完成')
    await fetchRequirements()
  } catch (error) {
    console.error('完成需求失败:', error)
    ElMessage.error('完成需求失败')
  }
}

// 拒绝需求
const handleReject = async (item: RequirementItem) => {
  try {
    const { value: reason } = await ElMessageBox.prompt('请输入拒绝原因', '拒绝需求', {
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    
    if (!reason) return
    
    const response = await requestV2.post(`/system/custom/requirements/${item.id}/status`, JSON.stringify({
        status: 'rejected',
        reject_reason: reason
      }))
    
    if (!(response.data.code == 200)) {
      throw new Error('拒绝需求失败')
    }
    
    ElMessage.success('需求已拒绝')
    await fetchRequirements()
  } catch (error) {
    console.error('拒绝需求失败:', error)
    ElMessage.error('拒绝需求失败')
  }
}

// 删除需求
const handleDelete = async (item: RequirementItem) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这条需求吗？此操作不可恢复。',
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    const response = await requestV2.post(`/system/custom/requirements/${item.id}`)
    
    if (!(response.data.code == 200)) {
      throw new Error('删除需求失败')
    }
    
    ElMessage.success('需求已删除')
    await fetchRequirements()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除需求失败:', error)
      ElMessage.error('删除需求失败')
    }
  }
}

// 获取状态类型
const getStatusType = (status: string) => {
  const typeMap = {
    pending: 'info',
    submitted: 'info',     // 兼容旧状态名
    processing: 'warning',
    accepted: 'warning',   // 兼容旧状态名
    completed: 'success',
    rejected: 'danger'
  }
  return typeMap[status as keyof typeof typeMap] || ''
}

// 获取状态文本
const getStatusText = (status: string) => {
  const textMap = {
    pending: '待处理',
    submitted: '待处理',   // 兼容旧状态名
    processing: '处理中',
    accepted: '处理中',    // 兼容旧状态名
    completed: '已完成',
    rejected: '已拒绝'
  }
  return textMap[status as keyof typeof textMap] || status
}

// 处理图片加载错误
const handleImageError = (err: Error) => {
  console.error('图片加载失败:', err)
}

// 格式化时间
const formatTime = (time: string) => {
  return new Date(time).toLocaleString()
}

// 处理图片数据
const getImages = (images: string | string[] | null): string[] => {
  if (!images) return [];
  if (Array.isArray(images)) return images;
  try {
    const parsed = JSON.parse(images);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [images]; // 如果不是JSON字符串，就当作单个图片URL处理
  }
}

onMounted(async () => {
  console.log('组件已挂载，先检查权限...')
  const authCheck = await checkAuth()
  console.log('权限检查完成，开始获取数据...')
  fetchRequirements()
})
</script>

<style scoped>
.requirement-management {
  padding: 20px;
}

.loading-state {
  text-align: center;
  padding: 40px;
}

.error-message {
  margin-bottom: 20px;
}

.stats-info {
  margin-bottom: 20px;
  color: #666;
}

.requirements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
  padding: 0 20px;
}

.requirement-card {
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.user-info {
  margin-bottom: 15px;
}

.user-info p {
  margin: 5px 0;
}

.description {
  color: #666;
  margin: 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.time {
  color: #999;
  font-size: 12px;
}

.preview-images {
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding: 10px 0;
  overflow-x: auto;
  scrollbar-width: thin;
}

.preview-images::-webkit-scrollbar {
  height: 6px;
}

.preview-images::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.preview-image {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
}

.card-actions {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.el-button-group {
  display: flex;
  gap: 4px;
}

.is-rejected {
  opacity: 0.8;
}

/* 详情弹窗样式 */
.requirement-detail {
  padding: 20px;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h4 {
  margin-bottom: 15px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.description-box {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-top: 10px;
}

.detail-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.detail-images .el-image {
  width: 100%;
  height: 150px;
  border-radius: 4px;
}
</style> 